import styles from './PortsPage.module.scss';
import { UserI } from 'app/utils/userSlice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Pagination } from 'app/components';

import { columns as tableColumns } from './columns';

import { throttle } from 'app/helpers';
import { useGetPortCatalogMutation } from 'app/api/marine-infrastructure';
import { PortCatalogSearchRequest,  PortCatalogOrderParams } from 'ais-facade-api/api';
import { Button, Input, PageHeader } from 'app/components';
import { useDebounce, useLocalStorage, useOnClickOutside } from 'app/hooks';

import { PortsFilterPanel } from 'app/components/FilterPanel';
import { Icon } from 'app/components/Icon';
import { Typo } from 'app/components/Typo';
import { getI18n, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { setPortsCatalogFilter } from 'app/utils/facadeReducer';
interface Props {
  user: UserI | null;
}

const initialBody: PortCatalogSearchRequest = {
  filter: {
    country: [],
    key: [],
  },
  order: {
    direction: 'ASC',
    by: 'CITY_NAME',
  },
  pagination: {
    offset: 0,
    limit: 20,
  },
};

export interface PortsSortingInterface {
  direction: 'ASC' | 'DESC' | 'default';
  by: string;
}

export interface Filters {
  touched?: boolean;
  country?: string[];
  countryAll: boolean;
}

export const PortsPage = ({ user }: Props) => {
  const { t } = useTranslation();
  const language = useMemo(() => getI18n().language, [t]);
  const dispatch = useAppDispatch();

  const [getCatalog, { data: catalog, ...catalogRequest }] = useGetPortCatalogMutation({
    fixedCacheKey: 'get-ports-mutation',
  });

  const debouncedGetCatalogHandler = useCallback(throttle(getCatalog, 200), []);

  const [currentPage, setCurrentPage] = useState(1);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const filters = useAppSelector((state) => state.facadeSlice.portsCatalogFilter);

  const [localFilter, setLocalFilter] = useLocalStorage<Filters | null>('filters-portsCatalog', null);

  const filterPanelRef = useRef<HTMLDivElement>(null);

  const clickOutsideHandler = () => {
    setShowFilter(false);
  };
  useOnClickOutside(filterPanelRef, clickOutsideHandler);

  const initialSort: PortCatalogOrderParams = {
    direction: 'ASC',
    by: 'CITY_NAME',
  };

  useEffect(() => {
    if (localFilter !== null) {
      dispatch(setPortsCatalogFilter(localFilter));
    }
  }, [localFilter]);

  const [sorting, setSorting] = useState<PortsSortingInterface>(initialSort);
  const [term, setTerm] = useLocalStorage<string>('portsCatalogTerm', '');

  const debouncedInputValue = useDebounce<string>(term, 500);

  const columns = useMemo(() => tableColumns, [t]);

  useEffect(() => {
    catalogRequest.reset();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    debouncedGetCatalogHandler({
      ...initialBody,
      filter: {
        country: filters?.countryAll ? [] : filters?.country,
        term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
      },
      pagination: {
        offset: 0,
        limit: 20,
      },
      order:
        sorting.direction === 'default'
          ? initialSort
          : {
            direction: sorting.direction,
            by: sorting.by,
          },
    });
  }, [debouncedInputValue, language]);

  useEffect(() => {
    if (user) {
      debouncedGetCatalogHandler({
        ...initialBody,
        filter: {
          term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
          country: filters?.countryAll ? [] : filters?.country,
        },

        pagination: {
          offset: currentOffset,
          limit: 20,
        },
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
              direction: sorting.direction,
              by: sorting.by,
            },
      });
    }
  }, [currentOffset, user]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);

      debouncedGetCatalogHandler({
        ...initialBody,
        filter: {
          term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
          country: filters?.countryAll ? [] : filters?.country,
        },
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
              direction: sorting.direction,
              by: sorting.by,
            },
      });
    }
  }, [sorting]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      setSorting(initialBody.order);

      debouncedGetCatalogHandler({
        ...initialBody,
        filter: {
          country: filters?.countryAll ? [] : filters?.country,
          term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
        },
      });
    }
  }, [filters]);

  return (
    <div className={styles.page}>
      <PageHeader iconName="catalog-ports" title={t('PortsPageHeader')} />
      <div className={styles.table_actions}>
        <Typo
          variant="span"
          size="m"
          value={`${t('CatalogPortsTotal')}${filters?.touched ? ` ${t('AppliedFilters')}:` : ':'}  ${catalog !== undefined ? catalog.total : '–'
            }`}
        />

        <Button variant="general" onClick={() => setShowFilter(true)} className={styles.icon_button}>
          {filters?.touched ? <Icon name="filter-active" /> : <Icon name="filter" />}

          <Typo value={t('Filter')} variant="p" />
        </Button>
        <Input
          className={styles.table_search}
          type="search"
          onChange={(e) => {
            setTerm(e.target.value);
            localStorage.setItem('portsCatalogTerm', JSON.stringify(debouncedInputValue));
          }}
          placeholder={t('SearchPlaceholder')}
          value={term}
        />
      </div>

      {catalogRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}

      {!catalogRequest.isLoading && catalog?.results?.length === 0 && <p className={styles.no_data}>{t('NoResultsFound')}</p>}

      {catalog?.results && catalog?.total && catalog.results.length > 0 ? (
        <>
          <div className={styles.table_container}>
            <Table manualSort={true} sorting={sorting} setSorting={setSorting} columns={columns} data={catalog.results} />
          </div>
          <div className={styles.pagination_container}>
            <Pagination
              totalCount={catalog.total}
              currentPage={currentPage}
              pageChangeHandler={setCurrentPage}
              rowsPerPage={20}
            />
          </div>
        </>
      ) : null}
      {showFilter && (
        <PortsFilterPanel
          onClose={() => {
            setShowFilter(false);
          }}
          filters={localFilter}
          handleFilters={setLocalFilter}
          innerRef={filterPanelRef}
        />
      )}
    </div>
  );
};
